<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const allLabels = useMapGetter('labels/getLabels');
const labelOptions = computed(() =>
  allLabels.value.map(label => ({ value: label.title, label: label.title }))
);

const selectedLabels = ref([]);

const dialogRef = ref(null);
const fileInput = ref(null);

const hasSelectedFile = ref(null);
const selectedFileName = ref('');

const validation = ref(null);
const mode = computed(() => {
  if (!validation.value) return 'normal';
  if (validation.value.errors?.length) return 'error';
  if (validation.value.warnings?.length) return 'warning';
  return 'normal';
});

const fallbackSampleUrl = '/downloads/import-contacts-sample.csv';
const csvUrl = computed(
  () => validation.value?.sample_csv_url || fallbackSampleUrl
);
const helpUrl = computed(() => validation.value?.help_url || '');

const VALIDATION_KEY =
  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.VALIDATION';

const dialogTitle = computed(() => {
  if (mode.value === 'error') return t(`${VALIDATION_KEY}.ERROR_TITLE`);
  if (mode.value === 'warning') return t(`${VALIDATION_KEY}.WARNING_TITLE`);
  return t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE');
});

const confirmLabel = computed(() => {
  if (mode.value === 'error') return t(`${VALIDATION_KEY}.RESET_FILE`);
  if (mode.value === 'warning') return t(`${VALIDATION_KEY}.IMPORT_ANYWAY`);
  return t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT');
});

const collectSuggestions = computed(() => {
  if (!validation.value) return [];
  const fromErrors = (validation.value.errors || []).flatMap(
    e => e.suggestions || []
  );
  const fromWarnings = (validation.value.warnings || [])
    .filter(w => w.found && w.suggested)
    .map(w => ({ found: w.found, suggested: w.suggested }));
  const all = [...fromErrors, ...fromWarnings];
  return all.filter(
    (s, idx) =>
      all.findIndex(o => o.found === s.found && o.suggested === s.suggested) ===
      idx
  );
});

const formatSuggestion = ({ found, suggested }) =>
  t(`${VALIDATION_KEY}.SUGGESTION_LINE`, { found, suggested });

const acceptedHeadersText = computed(() => {
  const ah = validation.value?.accepted_headers;
  if (!ah) return '';
  const requiredLabel = t(`${VALIDATION_KEY}.REQUIRED_LABEL`);
  const req = (ah.required || []).map(h => `${h} (${requiredLabel})`);
  const opt = ah.optional || [];
  return [...req, ...opt].join(', ');
});

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
  validation.value = null;
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) fileInput.value.value = null;
  selectedFileName.value = '';
  validation.value = null;
};

const handleResetFile = () => {
  handleRemoveFile();
};

const handleConfirm = () => {
  if (mode.value === 'error') {
    handleResetFile();
    return;
  }
  if (!hasSelectedFile.value) return;
  emit('import', {
    file: hasSelectedFile.value,
    labels: selectedLabels.value,
    confirm: mode.value === 'warning',
  });
};

const handleClose = () => {
  handleRemoveFile();
};

const setValidationResult = payload => {
  validation.value = payload || null;
};

const reset = () => {
  handleRemoveFile();
};

defineExpose({ dialogRef, setValidationResult, reset });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :confirm-button-label="confirmLabel"
    :is-loading="isImportingContact"
    :disable-confirm-button="
      isImportingContact || (mode === 'normal' && !hasSelectedFile)
    "
    width="xl"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <template #titleAddon>
      <a
        :href="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TUTORIAL_URL')"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-text hover:underline"
      >
        <fluent-icon icon="info" size="14" class="flex-shrink-0 mr-1 text-n-blue-text" />
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TUTORIAL') }}
      </a>
    </template>
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-text"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <div v-if="mode !== 'normal'" class="flex flex-col gap-4">
      <div v-if="validation?.message" class="text-sm text-n-slate-12">
        {{ validation.message }}
      </div>

      <div v-if="validation?.errors?.length" class="flex flex-col gap-1">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t(`${VALIDATION_KEY}.ERRORS_HEADING`) }}
        </p>
        <ul class="pl-5 m-0 text-sm text-n-ruby-11 list-disc">
          <li
            v-for="err in validation.errors"
            :key="err.code + (err.field || '')"
          >
            {{ err.message }}
          </li>
        </ul>
      </div>

      <div v-if="validation?.warnings?.length" class="flex flex-col gap-1">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t(`${VALIDATION_KEY}.WARNINGS_HEADING`) }}
        </p>
        <ul class="pl-5 m-0 text-sm text-n-amber-11 list-disc">
          <li v-for="w in validation.warnings" :key="w.code + (w.found || '')">
            {{ w.message }}
          </li>
        </ul>
      </div>

      <div v-if="collectSuggestions.length" class="flex flex-col gap-1">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t(`${VALIDATION_KEY}.SUGGESTIONS_HEADING`) }}
        </p>
        <ul class="pl-5 m-0 text-sm text-n-slate-11 list-disc">
          <li
            v-for="s in collectSuggestions"
            :key="`${s.found}->${s.suggested}`"
          >
            {{ formatSuggestion(s) }}
          </li>
        </ul>
      </div>

      <div
        v-if="validation?.accepted_headers"
        class="flex flex-col gap-1 p-3 rounded-md bg-n-alpha-2"
      >
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t(`${VALIDATION_KEY}.ACCEPTED_HEADERS_HEADING`) }}
        </p>
        <p class="m-0 font-mono text-sm text-n-slate-11">
          {{ acceptedHeadersText }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t(`${VALIDATION_KEY}.OPTIONAL_NOTE`) }}
        </p>
      </div>

      <div class="flex gap-4 text-sm">
        <a
          v-if="helpUrl"
          :href="helpUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-blue-text"
        >
          {{ t(`${VALIDATION_KEY}.HELP_LINK`) }}
        </a>
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-text"
        >
          {{ t(`${VALIDATION_KEY}.SAMPLE_LINK`) }}
        </a>
      </div>
    </div>

    <div v-else class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')
            "
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2 mt-2">
      <label class="text-sm text-n-slate-12 whitespace-nowrap">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="selectedLabels"
        :options="labelOptions"
        :placeholder="
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_PLACEHOLDER')
        "
        :empty-state="
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_EMPTY')
        "
        :disabled="isImportingContact"
      />
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />

    <template v-if="mode === 'warning'" #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          class="w-full"
          type="button"
          @click="dialogRef?.close()"
        />
        <Button
          :label="t(`${VALIDATION_KEY}.RESET_FILE`)"
          variant="ghost"
          color="slate"
          class="w-full"
          type="button"
          @click="handleResetFile"
        />
        <Button
          :label="t(`${VALIDATION_KEY}.IMPORT_ANYWAY`)"
          color="blue"
          class="w-full"
          :is-loading="isImportingContact"
          :disabled="isImportingContact"
          type="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
